/**
 * Detects unit-driven hand order grants that should play the open-box VFX.
 *
 * Includes deck draws (unknown card from deck) and random pool grants from units.
 * Excludes strategems, completed-order recalls, and fixed known-order grants (see fly sources).
 */

/** addCardToHand `source` values eligible for box animation */
const HAND_ORDER_GRANT_BOX_SOURCES = new Set([
  'supply_marshal',
  'heqet_turn_end',
  'versatile_survivalist',
  'counterintel_specialist',
  'knowledge_keeper',
  'exiled_strategist',
  'the_unyielding',
  'exolot_looter',
  'mighty_juggernaut',
  'shadow_sniper',
  'lester_brahms',
  'waste_disposal_unit_x3',
  'veilpiercer_raid',
  'kendrex_scavenger_raid',
  'blight_machinist_raid',
  'fell_unit_draw',
  'sha_deploy',
  'sha_fell',
  'heavy_machinery_care_taker',
  'redeem_unit_147',
  'redeem_unit_308',
  'redeem_unit_367',
]);

/** Fixed known-order grants: fly miniature card from provider unit to hand slot. */
const HAND_ORDER_GRANT_FLY_SOURCES = new Set([
  'business_guy',
  'survival_architect_shield',
  'scavenger_operative',
  'exolot_armsmaster',
  'stinger_commander',
  'unit_164_turn_start',
  'flesh_mimic',
  'plague_of_lice_and_gnats',
  'essia_luhr',
]);

function isHandOrderGrantBoxSource(source) {
  return typeof source === 'string' && HAND_ORDER_GRANT_BOX_SOURCES.has(source);
}

function isHandOrderGrantFlySource(source) {
  return typeof source === 'string' && HAND_ORDER_GRANT_FLY_SOURCES.has(source);
}

function buildHandOrderGrantTriggerKey(entry) {
  if (!entry?.orderInstanceId || !entry?.providedBy?.instanceId) {
    throw new Error('buildHandOrderGrantTriggerKey: orderInstanceId and providedBy.instanceId are required');
  }
  return `hand-grant:${entry.providedBy.instanceId}:${entry.orderInstanceId}`;
}

function collectHandOrderGrantTriggerKeysForKind(gameState, kind) {
  if (!gameState || !gameState.state) {
    throw new Error('collectHandOrderGrantTriggerKeysForKind: gameState.state is required');
  }
  if (kind !== 'box' && kind !== 'fly') {
    throw new Error(`collectHandOrderGrantTriggerKeysForKind: invalid kind ${kind}`);
  }
  const matchesSource = kind === 'box' ? isHandOrderGrantBoxSource : isHandOrderGrantFlySource;

  const allPlayerIds = gameState.players || Object.keys(gameState.state || {});
  const keys = new Set();

  allPlayerIds.forEach((playerId) => {
    const player = gameState.state?.[playerId];
    if (!player || !Array.isArray(player.providedOrders)) {
      return;
    }

    player.providedOrders.forEach((entry) => {
      if (!entry?.providedBy || entry.providedBy.type !== 'unit') {
        return;
      }
      if (!matchesSource(entry.source)) {
        return;
      }
      keys.add(buildHandOrderGrantTriggerKey(entry));
    });
  });

  return keys;
}

function collectHandOrderGrantTriggerKeys(gameState) {
  return collectHandOrderGrantTriggerKeysForKind(gameState, 'box');
}

function collectHandOrderGrantFlyTriggerKeys(gameState) {
  return collectHandOrderGrantTriggerKeysForKind(gameState, 'fly');
}

function hasNewHandOrderGrantTrigger(previousKeys, currentKeys) {
  if (!(previousKeys instanceof Set)) {
    throw new Error('hasNewHandOrderGrantTrigger: previousKeys must be a Set');
  }
  if (!(currentKeys instanceof Set)) {
    throw new Error('hasNewHandOrderGrantTrigger: currentKeys must be a Set');
  }

  for (const key of currentKeys) {
    if (!previousKeys.has(key)) {
      return true;
    }
  }
  return false;
}

function findNewHandOrderGrantEntriesForKind(gameState, previousKeys, kind) {
  if (!gameState || !gameState.state) {
    throw new Error('findNewHandOrderGrantEntriesForKind: gameState.state is required');
  }
  if (!(previousKeys instanceof Set)) {
    throw new Error('findNewHandOrderGrantEntriesForKind: previousKeys must be a Set');
  }
  if (kind !== 'box' && kind !== 'fly') {
    throw new Error(`findNewHandOrderGrantEntriesForKind: invalid kind ${kind}`);
  }
  const matchesSource = kind === 'box' ? isHandOrderGrantBoxSource : isHandOrderGrantFlySource;

  const entries = [];
  const allPlayerIds = gameState.players || Object.keys(gameState.state || {});

  allPlayerIds.forEach((playerId) => {
    const player = gameState.state?.[playerId];
    if (!player || !Array.isArray(player.providedOrders)) {
      return;
    }

    player.providedOrders.forEach((entry) => {
      if (!entry?.providedBy || entry.providedBy.type !== 'unit') {
        return;
      }
      if (!matchesSource(entry.source)) {
        return;
      }
      const key = buildHandOrderGrantTriggerKey(entry);
      if (!previousKeys.has(key)) {
        entries.push({
          key,
          playerId,
          receivingPlayerId: playerId,
          providedBy: entry.providedBy,
          orderInstanceId: entry.orderInstanceId,
          orderId: entry.orderId,
          orderName: entry.orderName,
          source: entry.source,
        });
      }
    });
  });

  return entries;
}

function findNewHandOrderGrantEntries(gameState, previousKeys) {
  return findNewHandOrderGrantEntriesForKind(gameState, previousKeys, 'box');
}

function findNewHandOrderGrantFlyEntries(gameState, previousKeys) {
  return findNewHandOrderGrantEntriesForKind(gameState, previousKeys, 'fly');
}

/**
 * Resolve battlefield slot for the granting unit (current board, then optional fallback map).
 */
function resolveProviderUnitSlot(gameState, providedBy, viewerUserId, previousUnitsByInstanceId = null) {
  if (!providedBy?.instanceId) {
    throw new Error('resolveProviderUnitSlot: providedBy.instanceId is required');
  }

  const instanceId = providedBy.instanceId;
  const allPlayerIds = gameState.players || Object.keys(gameState.state || {});

  for (const playerId of allPlayerIds) {
    const player = gameState.state?.[playerId];
    if (!player?.playedOrders) {
      continue;
    }

    for (const zone of ['front', 'flank']) {
      const row = player.playedOrders[zone];
      if (!Array.isArray(row)) {
        continue;
      }
      const index = row.findIndex((u) => u && u.instanceId === instanceId);
      if (index !== -1) {
        const zonePrefix = String(playerId) === String(viewerUserId) ? 'your' : 'opponent';
        return {
          playerId,
          zone: `${zonePrefix}_${zone}`,
          index,
          isPlayerUnit: String(playerId) === String(viewerUserId),
        };
      }
    }
  }

  if (previousUnitsByInstanceId instanceof Map && previousUnitsByInstanceId.has(instanceId)) {
    const prev = previousUnitsByInstanceId.get(instanceId);
    const zonePrefix = String(prev.playerId) === String(viewerUserId) ? 'your' : 'opponent';
    return {
      playerId: prev.playerId,
      zone: `${zonePrefix}_${prev.zone}`,
      index: prev.index,
      isPlayerUnit: String(prev.playerId) === String(viewerUserId),
    };
  }

  return null;
}

function normalizePendingHandOrderGrantFlyKey(anim) {
  if (!anim?.grantedOrderInstanceId) {
    throw new Error('normalizePendingHandOrderGrantFlyKey: grantedOrderInstanceId is required');
  }
  const providerInstanceId =
    anim.providerInstanceId ||
    (typeof anim.animationKey === 'string' ? anim.animationKey.split(':')[1] : null);
  if (!providerInstanceId) {
    throw new Error(
      `normalizePendingHandOrderGrantFlyKey: provider instanceId required for ${anim.animationKey}`
    );
  }
  return buildHandOrderGrantTriggerKey({
    orderInstanceId: anim.grantedOrderInstanceId,
    providedBy: { instanceId: providerInstanceId },
  });
}

function buildHandOrderGrantFlyOrderSnapshot(anim) {
  if (anim.grantedOrderInstanceId == null) {
    throw new Error('buildHandOrderGrantFlyOrderSnapshot: grantedOrderInstanceId is required');
  }
  if (anim.grantedOrderId == null) {
    throw new Error('buildHandOrderGrantFlyOrderSnapshot: grantedOrderId is required');
  }
  return {
    id: anim.grantedOrderId,
    name: anim.grantedOrderName || `Order ${anim.grantedOrderId}`,
    instanceId: anim.grantedOrderInstanceId,
    type: anim.grantedOrderType || 'unit',
    isPlayable: true,
    isOOR: false,
  };
}

function resolveGeneralZoneForViewer(providerPlayerId, viewerUserId) {
  if (providerPlayerId == null) {
    throw new Error('resolveGeneralZoneForViewer: providerPlayerId is required');
  }
  if (viewerUserId == null) {
    throw new Error('resolveGeneralZoneForViewer: viewerUserId is required');
  }
  return String(providerPlayerId) === String(viewerUserId) ? 'player_general' : 'opponent_general';
}

/**
 * Convert server pendingHandOrderGrantFlyAnimations to viewer-relative fly grant payloads.
 * Mutates gameStateFragment by removing the pending array (same pattern as deploy VFX).
 */
function consumePendingHandOrderGrantFlyAnimations(gameStateFragment, viewerUserId) {
  if (!gameStateFragment?.pendingHandOrderGrantFlyAnimations?.length) {
    return [];
  }
  if (viewerUserId == null) {
    throw new Error('consumePendingHandOrderGrantFlyAnimations: viewerUserId is required');
  }

  const pending = [...gameStateFragment.pendingHandOrderGrantFlyAnimations];
  delete gameStateFragment.pendingHandOrderGrantFlyAnimations;

  return pending
    .filter((anim) => String(anim.receivingPlayerId) === String(viewerUserId))
    .map((anim) => {
      if (!anim.animationKey) {
        throw new Error('consumePendingHandOrderGrantFlyAnimations: animationKey is required');
      }

      const baseGrant = {
        key: normalizePendingHandOrderGrantFlyKey(anim),
        orderId: anim.grantedOrderId,
        orderName: anim.grantedOrderName,
        orderInstanceId: anim.grantedOrderInstanceId,
        source: anim.source,
        orderSnapshot: buildHandOrderGrantFlyOrderSnapshot(anim),
        destinationType: anim.destinationType,
      };

      if (anim.providerOrigin === 'general') {
        if (anim.providerPlayerId == null) {
          throw new Error(
            `consumePendingHandOrderGrantFlyAnimations: providerPlayerId required for general origin ${anim.animationKey}`
          );
        }
        const generalGrant = {
          ...baseGrant,
          originType: 'general',
          generalZone: resolveGeneralZoneForViewer(anim.providerPlayerId, viewerUserId),
        };
        if (anim.destinationType === 'deploy_slot') {
          if (anim.deployZone !== 'front' && anim.deployZone !== 'flank') {
            throw new Error(
              `consumePendingHandOrderGrantFlyAnimations: deployZone must be front or flank for ${anim.animationKey}`
            );
          }
          if (anim.deploySlotIndex == null) {
            throw new Error(
              `consumePendingHandOrderGrantFlyAnimations: deploySlotIndex required for ${anim.animationKey}`
            );
          }
          return {
            ...generalGrant,
            destinationZone: `your_${anim.deployZone}`,
            destinationIndex: anim.deploySlotIndex,
          };
        }
        return generalGrant;
      }

      if (anim.providerZone == null || anim.providerSlotIndex == null) {
        throw new Error(
          `consumePendingHandOrderGrantFlyAnimations: provider slot required for ${anim.animationKey}`
        );
      }
      const zonePrefix =
        String(anim.providerPlayerId) === String(viewerUserId) ? 'your' : 'opponent';
      return {
        ...baseGrant,
        zone: `${zonePrefix}_${anim.providerZone}`,
        index: anim.providerSlotIndex,
      };
    });
}

export {
  HAND_ORDER_GRANT_BOX_SOURCES,
  HAND_ORDER_GRANT_FLY_SOURCES,
  isHandOrderGrantBoxSource,
  isHandOrderGrantFlySource,
  buildHandOrderGrantTriggerKey,
  collectHandOrderGrantTriggerKeys,
  collectHandOrderGrantFlyTriggerKeys,
  hasNewHandOrderGrantTrigger,
  findNewHandOrderGrantEntries,
  findNewHandOrderGrantFlyEntries,
  resolveProviderUnitSlot,
  resolveGeneralZoneForViewer,
  consumePendingHandOrderGrantFlyAnimations,
};
